import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Home, CheckCircle, Clock, XCircle,
  ClipboardList, Briefcase, Calendar, User, AlertCircle, Download,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatTimeFromDate } from '../../utils/dateFormatting';

const StatusBadge = ({ status }) => {
  const map = {
    submitted: { cls: 'bg-green-500/20 text-green-400', icon: CheckCircle, label: 'Submitted' },
    approved: { cls: 'bg-blue-500/20 text-blue-400', icon: CheckCircle, label: 'Approved' },
    rejected: { cls: 'bg-red-500/20 text-red-400', icon: XCircle, label: 'Rejected' },
    pending: { cls: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'Pending Approval' },
    draft: { cls: 'bg-gray-500/20 text-[var(--m-text-secondary)]', icon: Clock, label: 'Draft' },
  };
  const s = map[status] || map.submitted;
  const Icon = s.icon;
  return <Badge className={s.cls}><Icon className="h-3 w-3 mr-1" />{s.label}</Badge>;
};

const FieldValue = ({ field, value, tenantSettings }) => {
  const ft = field.field_type || field.type;

  if (ft === 'section_header' || ft === 'section') {
    return (
      <div className="pt-4 pb-1 border-b border-[var(--m-border)]">
        <p className="text-sm font-semibold text-[#ffd108]">{field.label}</p>
      </div>
    );
  }

  const empty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);

  const renderValue = () => {
    if (empty) return <span className="text-[var(--m-text-muted)] italic text-sm">Not answered</span>;

    switch (ft) {
      case 'text':
      case 'number':
        return <p className="text-[var(--m-text)] text-sm">{String(value)}</p>;

      case 'textarea':
        return <p className="text-[var(--m-text)] text-sm whitespace-pre-wrap">{value}</p>;

      case 'date':
        try { return <p className="text-[var(--m-text)] text-sm">{format(new Date(value), 'dd/MM/yyyy')}</p>; }
        catch { return <p className="text-[var(--m-text)] text-sm">{value}</p>; }

      case 'time':
        return <p className="text-[var(--m-text)] text-sm">{value}</p>;

      case 'yes_no':
        return (
          <Badge className={value === true || value === 'yes' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
            {value === true || value === 'yes' ? 'Yes' : 'No'}
          </Badge>
        );

      case 'checkbox':
        return (
          <Badge className={value ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-[var(--m-text-secondary)]'}>
            {value ? 'Checked' : 'Unchecked'}
          </Badge>
        );

      case 'select':
        return <p className="text-[var(--m-text)] text-sm">{value}</p>;

      case 'multi_select':
        return (
          <div className="flex flex-wrap gap-1">
            {(Array.isArray(value) ? value : [value]).map((v, i) => (
              <Badge key={i} variant="secondary" className="text-xs">{v}</Badge>
            ))}
          </div>
        );

      case 'photo':
        return value ? (
          <img src={value} alt={field.label} className="max-w-full max-h-48 rounded-lg object-contain bg-black/30" />
        ) : null;

      case 'multi_photo':
        return (
          <div className="flex flex-wrap gap-2">
            {(Array.isArray(value) ? value : []).map((url, i) => (
              <img key={i} src={url} alt={`${field.label} ${i + 1}`} className="w-20 h-20 rounded-lg object-cover bg-black/30" />
            ))}
          </div>
        );

      case 'signature':
        return value ? (
          <img src={value} alt="Signature" className="max-w-full max-h-24 rounded-lg bg-white p-1" />
        ) : null;

      case 'repeating_group': {
        if (!Array.isArray(value) || value.length === 0)
          return <span className="text-[var(--m-text-muted)] italic text-sm">No rows</span>;
        const cols = field.columns || [];
        return (
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[var(--m-border)]">
                  {cols.map((c, ci) => (
                    <th key={ci} className="px-2 py-1 text-left text-[var(--m-text-secondary)] font-medium">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {value.map((row, ri) => (
                  <tr key={ri} className="border-b border-[var(--m-border)]">
                    {cols.map((c, ci) => (
                      <td key={ci} className="px-2 py-1.5 text-[var(--m-text)]">{row[c.id] ?? row[c.label] ?? '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      }

      default:
        return <p className="text-[var(--m-text)] text-sm">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</p>;
    }
  };

  return (
    <div className="py-3" data-testid={`field-${field.id}`}>
      <p className="text-xs text-[var(--m-text-secondary)] mb-1">{field.label}{field.required ? ' *' : ''}</p>
      {renderValue()}
    </div>
  );
};

const MobileSubmissionDetail = () => {
  const navigate = useNavigate();
  const { submissionId } = useParams();
  const { api, tenantSettings } = useMobileAuth();
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get(`/api/form-submissions/${submissionId}`);
        setSubmission(res.data);
      } catch (err) {
        console.error('Failed to fetch submission:', err);
        toast.error('Failed to load submission');
      } finally {
        setLoading(false);
      }
    };
    if (submissionId) fetch();
  }, [api, submissionId]);

  const handleDownloadPdf = async () => {
    try {
      setDownloading(true);
      const res = await api.get(`/api/form-submissions/${submissionId}/pdf`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const filename = `${(submission?.template_name || 'form').replace(/\s+/g, '_')}_${submissionId.slice(0, 8)}.pdf`;

      const isNative = window.Capacitor?.isNativePlatform?.();
      const platform = window.Capacitor?.getPlatform?.();

      if (isNative) {
        const file = new File([blob], filename, { type: 'application/pdf' });

        // 1) Try Web Share API directly (works on iOS, newer Android)
        //    Skip canShare check — Android WebViews often report false
        //    even when share() would succeed.
        try {
          await navigator.share({ files: [file], title: filename });
          toast.success('PDF ready to save');
          return;
        } catch (shareErr) {
          if (shareErr?.name === 'AbortError') return; // user dismissed
          console.warn('navigator.share failed, trying fallback:', shareErr?.message);
        }

        // 2) Android fallback: write via Capacitor Filesystem then share the URI
        if (platform === 'android') {
          try {
            const base64Data = await new Promise((resolve, reject) => {
              const r = new FileReader();
              r.onload = () => resolve(r.result.split(',')[1]); // strip data:...;base64, prefix
              r.onerror = reject;
              r.readAsDataURL(blob);
            });
            const Filesystem = window.Capacitor.registerPlugin('Filesystem');
            const writeResult = await Filesystem.writeFile({
              path: `Download/${filename}`,
              data: base64Data,
              directory: 'EXTERNAL_STORAGE',
              recursive: true,
            });
            toast.success('PDF saved');
            // Try opening/sharing the saved file
            try {
              const Share = window.Capacitor.registerPlugin('Share');
              await Share.share({ url: writeResult.uri, title: filename, dialogTitle: 'Open PDF' });
            } catch { /* share optional */ }
            return;
          } catch (fsErr) {
            console.warn('Filesystem fallback failed:', fsErr?.message);
          }

          // 3) Android last resort: blob URL (sometimes works on newer WebViews)
          try {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            setTimeout(() => URL.revokeObjectURL(url), 10000);
            toast.success('PDF downloaded');
            return;
          } catch (dlErr) {
            console.warn('Blob download fallback failed:', dlErr?.message);
          }
        }

        // 4) Generic native fallback: data URI in new window
        const reader = new FileReader();
        reader.onload = () => {
          const win = window.open(reader.result, '_blank');
          if (!win) toast.error('Could not open PDF — try opening in your browser instead');
          else toast.success('PDF opened');
        };
        reader.readAsDataURL(blob);
      } else {
        // Standard browser: anchor download
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success('PDF downloaded');
      }
    } catch (err) {
      if (err?.name === 'AbortError') return;
      console.error('PDF download failed:', err);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  if (!submission) {
    return (
      <MobileLayout>
        <div className="min-h-full bg-[var(--m-bg)] flex flex-col items-center justify-center p-4">
          <AlertCircle className="h-12 w-12 text-[var(--m-text-muted)] mb-3" />
          <p className="text-[var(--m-text-secondary)]">Submission not found</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-[#ffd108] text-sm">Go back</button>
        </div>
      </MobileLayout>
    );
  }

  const template = submission.template;
  const fields = template?.fields || [];
  const data = submission.data || {};
  const ts = submission.submitted_at || submission.created_at;

  return (
    <MobileLayout>
      <div className="min-h-full bg-[var(--m-bg)]" data-testid="mobile-submission-detail">
        {/* Header */}
        <div className="bg-[var(--m-bg-card)] px-4 py-3 flex items-center justify-between sticky top-0 z-10 safe-top">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-[var(--m-text-secondary)] p-1" data-testid="back-btn">
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div className="flex items-center gap-2 min-w-0">
              <ClipboardList className="h-5 w-5 text-[#ffd108] flex-shrink-0" />
              <h1 className="text-[var(--m-text)] font-semibold truncate">{submission.template_name || 'Submission'}</h1>
            </div>
          </div>
          <button onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)] p-1" data-testid="home-btn">
            <Home className="h-5 w-5" />
          </button>
        </div>

        {/* Meta info */}
        <div className="px-4 py-3 bg-[var(--m-bg-card)]/50 border-b border-[var(--m-border)] space-y-2">
          <div className="flex items-center justify-between">
            <StatusBadge status={submission.approval_status || submission.status} />
            {submission.edit_count > 0 && (
              <span className="text-xs text-[var(--m-text-muted)]">Edited {submission.edit_count}x</span>
            )}
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--m-text-secondary)]">
            <span className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              {ts ? `${format(new Date(ts), 'dd/MM/yyyy')} ${formatTimeFromDate(new Date(ts), tenantSettings?.time_format)}` : '—'}
            </span>
            {submission.operator_name && (
              <span className="flex items-center gap-1">
                <User className="h-3 w-3" />
                {submission.operator_name}
              </span>
            )}
            {submission.job_description && (
              <span className="flex items-center gap-1">
                <Briefcase className="h-3 w-3" />
                {submission.job_description}
              </span>
            )}
          </div>

          {submission.approval_status === 'rejected' && submission.rejection_reason && (
            <div className="p-2 bg-red-500/10 border border-red-500/30 rounded text-xs text-red-400">
              <strong>Rejection reason:</strong> {submission.rejection_reason}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-4 py-3 border-b border-[var(--m-border)]">
          <button
            onClick={handleDownloadPdf}
            disabled={downloading}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-[#ffd108] text-black font-semibold rounded-lg active:scale-[0.98] transition-transform disabled:opacity-50"
            data-testid="download-pdf-btn"
          >
            {downloading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            {downloading ? 'Generating...' : 'Download PDF Copy'}
          </button>
        </div>

        {/* Fields */}
        <div className="px-4 pb-8 divide-y divide-gray-800">
          {fields.map((field) => {
            const ft = field.field_type || field.type;
            if (ft === 'section_header' || ft === 'section') {
              return <FieldValue key={field.id} field={field} value={null} tenantSettings={tenantSettings} />;
            }
            return (
              <FieldValue
                key={field.id}
                field={field}
                value={data[field.id]}
                tenantSettings={tenantSettings}
              />
            );
          })}

          {fields.length === 0 && (
            <div className="text-center text-[var(--m-text-muted)] py-8">
              <p>No field definitions available</p>
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
};

export default MobileSubmissionDetail;
